import React from 'react';
import _ from 'underscore';

import * as FileConstants from 'folder-items/FileConstants';
import {RequestResponseSample} from 'requests/RequestConstants';
import {useAllTagSchemas} from 'schemas/schemaGraphQLHooks';
import * as Headers from 'ui-library/Headers';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {getWorkflowDisplayString} from 'workflows/util/WorkflowUtil';

import {TagSchemaListingWithFieldsGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import ResponseSampleTable from './ResponseSampleTable';

interface BioentityTableProps {
  samples: Array<RequestResponseSample & {bioentity: FileConstants.BioentitySummary}>;
  schemaName: string;
}

function BioentityTable(props: BioentityTableProps): JSX.Element {
  return (
    <ResponseSampleTable
      header={<span>{props.schemaName} Entities</span>}
      records={props.samples.map(({bioentity, status}) => ({
        id: bioentity.name,
        createdAt: bioentity.created_at,
        status,
        tags: bioentity.tags,
        url: bioentity.editURL,
      }))}
    />
  );
}

/**
 * @param {Array[ResponseSample]} all response samples
 * @returns {Array[Array[ResponseSample]]} bioentity samples grouped and sorted by schema name
 */
function groupBioentitiesForTables(
  samples: Array<RequestResponseSample>,
  tagSchemasById: Record<string, TagSchemaListingWithFieldsGraphQL>
): Array<Array<RequestResponseSample & {bioentity: FileConstants.BioentitySummary}>> {
  const bioentitySamples = samples.filter((sample) => sample.bioentity) as Array<
    RequestResponseSample & {
      bioentity: FileConstants.BioentitySummary;
    }
  >;
  const bioentitySamplesBySchemaId = _.groupBy(bioentitySamples, ({bioentity}) => bioentity.tagSchema?.id);

  return _.sortBy(Object.keys(bioentitySamplesBySchemaId), (schemaId) => tagSchemasById[schemaId]?.name).map(
    (schemaId) => bioentitySamplesBySchemaId[schemaId]
  );
}

function Frame(props: {children: React.ReactNode}): JSX.Element {
  return (
    <div className="u-spacing-top u-spacing-bottom">
      <Headers.Title>
        {getWorkflowDisplayString('Legacy workflow output samples', 'Workflow output samples')}
      </Headers.Title>
      {props.children}
    </div>
  );
}

interface ResponseSamplesProps {
  samples: Array<RequestResponseSample>;
  tagSchemasById: Record<string, TagSchemaListingWithFieldsGraphQL>;
  tagSchemasLoading: boolean;
}

class ResponseSamples extends React.Component<ResponseSamplesProps> {
  _renderBioentitySampleTable(
    samples: Array<RequestResponseSample & {bioentity: FileConstants.BioentitySummary}>
  ): JSX.Element {
    // The schema name is needed to render the table informatively, but there are a lot of potential null
    // values along the way. Here we explicitly handle possible nulls to protect against an NPE on the name.
    const tagSchemaId: string | null = samples[0].bioentity.tagSchema?.id ?? null;
    const schemaName =
      tagSchemaId == null || this.props.tagSchemasById[tagSchemaId] == null
        ? '(Unknown)'
        : this.props.tagSchemasById[tagSchemaId].name;
    return <BioentityTable key={tagSchemaId} samples={samples} schemaName={schemaName} />;
  }

  render(): JSX.Element {
    if (this.props.samples.length === 0) {
      return (
        <Frame>
          <div className="u-faded">
            No samples in the terminal {getWorkflowDisplayString('legacy workflow', 'workflow')} stage output
            table
          </div>
        </Frame>
      );
    }

    if (this.props.tagSchemasLoading) {
      return (
        <Frame>
          <HorizontalThrobber />
        </Frame>
      );
    }

    const groupedBioentitiesForTables = groupBioentitiesForTables(
      this.props.samples,
      this.props.tagSchemasById
    );

    return (
      <Frame>{groupedBioentitiesForTables.map((samples) => this._renderBioentitySampleTable(samples))}</Frame>
    );
  }
}

export default function QueryingResponseSamples(
  props: Omit<ResponseSamplesProps, 'tagSchemasById' | 'tagSchemasLoading'>
): JSX.Element {
  const tagSchemas = useAllTagSchemas();
  const tagSchemasById = _.indexBy(tagSchemas ?? [], 'id');

  return (
    <ResponseSamples {...props} tagSchemasById={tagSchemasById} tagSchemasLoading={tagSchemas == null} />
  );
}
